import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FiHelpCircle } from 'react-icons/fi';
import {
  PageHeader,
  Button,
  HelpWindow,
  TooltipTour,
  TourStep,
  Tabs,
  TabList,
  Tab,
  SampleInformation,
  PcVariables,
  SampleMeasured,
  CapillaryPressurePanel,
  LiaoResults,
  SampleResults,
  Curves
} from '../../design-system';
import { listAllProjectSamples } from '../../api/data';
import { Sample } from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import {
  handleLiaoChartData,
} from '../../components/utils/sampleUtils';
import {
  parseLiaoAnalysisResultsToNonNullable,
  parseLiaoSampleResultsToNonNullable,
  parseSampleCurvesToNonNullable,
} from '../../utils';
import { ReactComponent as EmptyChartIllustration } from '../../assets/empty-chart-capillary-pressure.svg';
import { initChartData, emptyConfig } from './constants';
import LoadSampleModal from './LoadSampleModal';
import CapillaryPressureSummary from './CapillaryPressureSummary';
import ProjectShell from '../../components/layout/ProjectShell';
import { useSampleHistory } from './useSampleHistory';
import styles from './CapillaryPressurePage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

const CapillaryPressurePage = () => {
  const [activeTab, setActiveTab] = useState('sample-information');
  const [modalOpen, setModalOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [liaoChartData, setLiaoChartData] = useState(initChartData);
  const [sampleCount, setSampleCount] = useState<number | null>(null);

  const { selectedInfo, setSelectedInfo } = useSelected();
  const { updateData, data } = useAppContext();
  const location = useLocation();
  const projectUid = new URLSearchParams(location.search).get('project');

  const {
    history: sampleHistory,
    addToHistory,
    removeFromHistory
  } = useSampleHistory(projectUid);

  useEffect(() => {
    if (!projectUid) return;
    listAllProjectSamples(projectUid)
      .then(data => setSampleCount(Array.isArray(data) ? data.length : 0))
      .catch(() => setSampleCount(0));
  }, [projectUid]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!selectedInfo.sample) {
      setLiaoChartData(initChartData);
      return;
    }

    const { analysis_curves, analysis_values, input_sample_curves } = selectedInfo.sample;

    if (!analysis_curves.liao || !analysis_values.liao) {
      setLiaoChartData(initChartData);
      return;
    }

    const liaoAnalysisResults = parseLiaoAnalysisResultsToNonNullable(
      analysis_curves.liao,
    );
    const liaoSampleResults = parseLiaoSampleResultsToNonNullable(
      analysis_values.liao,
    );
    const sampleCurves = parseSampleCurvesToNonNullable(input_sample_curves);

    setLiaoChartData(initChartData);
    handleLiaoChartData(
      liaoAnalysisResults,
      liaoSampleResults,
      sampleCurves,
      setLiaoChartData,
    );
  }, [selectedInfo.sample]);

  const handleSelectSample = (sample: Sample, allSamples: Sample[], well?: Well) => {
    setSampleCount(allSamples.length);
    const wellSamples = allSamples.filter(s => s.well_uid === sample.well_uid);
    updateData({ wellSamples });
    const resolvedWell =
      well ||
      data.wells.find(entry => entry.uid === sample.well_uid) ||
      (selectedInfo.well?.uid === sample.well_uid ? selectedInfo.well : undefined);
    setSelectedInfo({ sample, well: resolvedWell });
    addToHistory(sample);
    setModalOpen(false);
  };

  const handleSelectMany = (picked: Sample[], allSamples: Sample[]) => {
    setSampleCount(allSamples.length);
    if (picked.length === 0) return;
    const last = picked[picked.length - 1];
    const wellSamples = allSamples.filter(s => s.well_uid === last.well_uid);
    updateData({ wellSamples });
    const resolvedWell =
      data.wells.find(entry => entry.uid === last.well_uid) ||
      (selectedInfo.well?.uid === last.well_uid ? selectedInfo.well : undefined);
    picked.forEach(s => addToHistory(s));
    setSelectedInfo({ sample: last, well: resolvedWell });
    setModalOpen(false);
  };

  const handleCloseTab = (closed: Sample) => {
    const list = sampleHistory;
    const idx = list.findIndex(s => s.uid === closed.uid);
    removeFromHistory(closed.uid);
    if (selectedInfo.sample?.uid !== closed.uid) return;
    const next = list[idx + 1] ?? list[idx - 1] ?? null;
    if (next) {
      setSelectedInfo({ sample: next, well: selectedInfo.well });
    } else {
      setSelectedInfo({ sample: undefined, well: undefined });
    }
  };

  const handleSelectRecent = async (sample: Sample) => {
    if (!projectUid) return;

    try {
      const allSamples = await listAllProjectSamples(projectUid);
      const normalizedSamples = Array.isArray(allSamples) ? allSamples : [];
      const fresh = normalizedSamples.find(s => s.uid === sample.uid);
      if (!fresh) {
        removeFromHistory(sample.uid);
        return;
      }
      handleSelectSample(fresh, normalizedSamples);
    } catch {
      removeFromHistory(sample.uid);
    }
  };

  const { title, subtitle } = emptyConfig['capillary-pressure'];

  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        target: '[data-tour="sidebar-header"]',
        title: 'Switch Project',
        content: 'Click here to quickly switch between your projects without leaving the page.'
      },
      {
        target: '[data-tour="sidebar"]',
        title: 'Navigation',
        content: 'Use the sidebar to switch between analyses in SCAL Bridge and GHE toolkit.'
      },
      {
        target: '[data-tour="load-sample-btn"]',
        title: 'Load a Sample',
        content: 'Start by loading a core sample from your project. Click here to browse available samples.'
      },
      {
        target: '[data-tour="page-header"]',
        title: 'Current Sample',
        content: 'Once a sample is loaded its name appears here. Click it at any time to switch samples.'
      }
    ],
    []
  );

  return (
    <ProjectShell>
      <div className={styles.stickyBar}>
        <PageHeader
          title="Capillary Pressure"
          sample={selectedInfo.sample ?? null}
          recentSamples={sampleHistory}
          sampleCount={sampleCount}
          onOpen={() => setModalOpen(true)}
          onSelectRecent={handleSelectRecent}
          onCloseTab={handleCloseTab}
          actions={
            <>
              {selectedInfo.sample ? (
                <Button variant="secondary" onClick={() => setSummaryOpen(true)}>
                  View Sample Summary
                </Button>
              ) : null}
              <Button
                variant="secondary"
                startIcon={<HelpIcon size={14} />}
                onClick={() => setHelpOpen(true)}
              >
                Help
              </Button>
            </>
          }
        />

        <nav className={styles.pageNav}>
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            variant="underline"
            size="md"
          >
            <TabList>
              <Tab value="sample-information">Sample Information</Tab>
              <Tab value="sample-measured">Sample Measurements</Tab>
              <Tab value="capillary-pressure">Capillary Pressure</Tab>
              <Tab value="sample-results">Sample Results</Tab>
              <Tab value="curves">Curves</Tab>
            </TabList>
          </Tabs>
        </nav>
      </div>

      {selectedInfo.sample ? (
        <div className={styles.content}>
          {activeTab === 'sample-information' && (
            <div className={styles.section}>
              <SampleInformation />
            </div>
          )}
          {activeTab === 'sample-measured' && (
            <div className={styles.section}>
              <PcVariables />
              <SampleMeasured />
            </div>
          )}
          {activeTab === 'capillary-pressure' && (
            <div className={styles.section}>
              <CapillaryPressurePanel />
              <LiaoResults
                liaoChartData={liaoChartData}
                isGeneratingPDF={false}
              />
            </div>
          )}
          {activeTab === 'sample-results' && (
            <div className={styles.section}>
              <SampleResults />
            </div>
          )}
          {activeTab === 'curves' && (
            <div className={styles.section}>
              <Curves />
            </div>
          )}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <EmptyChartIllustration style={{ width: '420px' }} />
          <p className={styles.emptyTitle}>{title}</p>
          <p className={styles.emptySubtitle}>{subtitle}</p>
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            Load Sample
          </Button>
        </div>
      )}

      <LoadSampleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleSelectSample}
        onSelectMany={handleSelectMany}
        projectUid={projectUid}
      />

      <CapillaryPressureSummary
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
      />

      <HelpWindow open={helpOpen} onClose={() => setHelpOpen(false)} />

      <TooltipTour
        tourKey="capillary-pressure-v1"
        steps={tourSteps}
      />
    </ProjectShell>
  );
};

export default CapillaryPressurePage;
